import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Box,
    Card,
    Typography,
    Stack,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    TextField,
    IconButton,
    Toolbar,
    InputAdornment
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Search,
    FilterList
} from '@mui/icons-material';
import categoriesApi from '../../../../services/categories';

const CategoriesBody = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const response = await categoriesApi.getAll();
                setCategories(response);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    const handleDelete = async (id) => {
        try {
            await categoriesApi.delete(id);
            setCategories(categories.filter(category => category.id !== id));
        } catch (error) {
            console.error('Error deleting category:', error);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const filteredCategories = (categories || []).filter(category =>
        category.catName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedSliders = stableSort(filteredCategories, getComparator(order, orderBy));
    const paginatedSliders = sortedSliders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


    const columns = [
        {
            id: 'serialNo',
            label: '#',
            width: '80px',
            sortable: false,
            format: (value, row, page, rowsPerPage, index) => (
                <Typography variant="body2">
                    {(page * rowsPerPage) + index + 1}
                </Typography>
            )
        },
        { id: 'catName', label: 'Name', width: '200px', sortable: true },
        { id: 'description', label: 'Description', width: '200px', sortable: true },
        {
            id: 'isActive',
            label: 'Status',
            width: '130px',
            align: 'center',
            sortable: true,
            format: (value) => (
                <Chip
                    label={value ? 'Active' : 'Inactive'}
                    color={value ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            id: 'actions',
            label: 'Actions',
            width: '120px',
            align: 'center',
            sortable: false,
            format: (value, row) => (
                <>
                    <IconButton
                        aria-label="edit"
                        onClick={() => navigate(`/admin/categories/${row.id}`, { state: { categoryData: row } })}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        onClick={() => handleDelete(row.id)}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </>
            )
        }
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '1.7rem' },
                        fontWeight: 600
                    }}
                >
                    Category Management
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/admin/categories/new')}
                    sx={{
                        boxShadow: 'none',
                        textTransform: 'capitalize',
                        borderRadius: '6px',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '6px 12px', sm: '8px 16px' }
                    }}
                >
                    New Category
                </Button>
            </Stack>

            <Card sx={{
                p: 2,
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                borderRadius: '12px'
            }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <Toolbar
                        sx={{
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                        }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px'
                                }
                            }}
                        />
                        <Box sx={{ flex: '1 1 100%' }} />
                        <IconButton>
                            <FilterList />
                        </IconButton>
                    </Toolbar>

                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={'medium'}
                        >
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align || 'left'}
                                            width={column.width}
                                            sortDirection={orderBy === column.id ? order : false}
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                backgroundColor: 'background.paper',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            {column.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === column.id}
                                                    direction={orderBy === column.id ? order : 'asc'}
                                                    onClick={() => handleRequestSort(column.id)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            ) : (
                                                column.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedSliders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            No sliders found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedSliders.map((row, index) => (
                                        <TableRow
                                            hover
                                            key={row.id}
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    borderBottom: '1px dashed',
                                                    borderColor: 'divider'
                                                }
                                            }}
                                        >
                                            {columns.map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.align}
                                                    >
                                                        {column.format
                                                            ? column.format(value, row, page, rowsPerPage, index)
                                                            : (
                                                                <Typography
                                                                    variant={column.id === 'id' ? 'subtitle2' : 'body2'}
                                                                    noWrap
                                                                >
                                                                    {value || '-'}
                                                                </Typography>
                                                            )
                                                        }
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        component="div"
                        count={filteredCategories.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Card>
        </Box>
    );
};

export default CategoriesBody;